// src/Component/AboutUs.tsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, CheckCircle, TrendingUp, Zap, ChevronDown, Scale, AlertTriangle } from 'lucide-react';
import about from '../assets/AboutUs.png';

const AboutUs = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "Motilal Algo kya hai?",
      answer: "Motilal Algo ek advanced algorithmic trading platform hai jo pre-defined strategies ke basis par automatically buy/sell orders execute karta hai, jisse manual errors aur emotional trading khatam ho jati hai."
    },
    {
      question: "Kya Algo Trading India me legal hai?",
      answer: "Haan, SEBI guidelines ke according India me Algo trading bilkul legal hai agar aap approved brokers aur APIs ka use kar rahe hain."
    },
    {
      question: "Kisko ye platform use karna chahiye?",
      answer: "Ye platform un sabhi retail traders aur investors ke liye hai jo data-driven trading karna chahte hain aur full-time screen ke saamne nahi baith sakte."
    },
    {
      question: "Kya isme loss hone ka risk hai?",
      answer: "Bilkul. Stock market me hamesha risk hota hai. Algo sirf strategy execute karta hai, market ki guarantee nahi deta. Isliye hamesha apni risk capacity ke hisaab se hi capital lagayein."
    }
  ];

  return (
    <div className="bg-slate-50 text-slate-900 font-[Urbanist] min-h-screen overflow-hidden">
      
      {/* =========================================
          1. HERO SECTION (Left Content, Right Image)
          ========================================= */}
      <section className="relative pt-32 pb-20 px-6 max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-12">
        {/* Left Content */}
        <motion.div 
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7 }}
          className="w-full md:w-1/2 flex flex-col items-start text-left"
        >
          <span className="text-blue-900 font-bold tracking-wider uppercase text-sm mb-4 flex items-center gap-2">
            <Zap size={16} /> Next-Gen Trading
          </span>
          <h1 className="text-5xl md:text-6xl font-black mb-6 tracking-tight leading-[1.1] text-slate-900">
            Smart Trading for the <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-orange-800">Next Generation.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed max-w-lg">
            Humara platform aapko lightning-fast algorithmic execution deta hai. Zero emotions, zero manual delays. Sirf pure data-driven results.
          </p>
          <button className="bg-red-700 hover:bg-red-600 text-white px-8 py-4 rounded-xl font-bold text-lg shadow-lg shadow-red-600/30 transition-all hover:-translate-y-1">
            Get Started Now →
          </button>
        </motion.div>

        {/* Right Image */}
        <motion.div 
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="w-full md:w-1/2 relative"
        >
          {/* Blue blur behind image for Razorpay feel */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] bg-blue-400/20 blur-[80px] rounded-full z-0"></div>
          <img 
            src= {about}
            alt="Algo Trading Tech" 
            className="relative z-10 w-full h-auto "
          />
        </motion.div>
      </section>

      {/* =========================================
          2. BLUE BANNER SECTION
          ========================================= */}
      <section className="bg-gradient-to-r from-blue-950 via-blue-900 to-indigo-800 py-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://tennews.in/wp-content/uploads/2023/08/1-trading-what-is-it.png')] opacity-40"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col md:flex-row justify-around items-center text-center gap-8">
          <div className="text-white">
            <h3 className="text-4xl font-black mb-1">10,000+</h3>
            <p className=" font-medium text-3xl">Active Traders</p>
          </div>
          <div className="w-px h-16 bg-blue-400/50 hidden md:block"></div>
          <div className="text-white">
            <h3 className="text-4xl font-black mb-1">99.9%</h3>
            <p className=" font-medium text-3xl">Server Uptime</p>
          </div>
          <div className="w-px h-16 bg-blue-400/50 hidden md:block"></div>
          <div className="text-white">
            <h3 className="text-4xl font-black mb-1">0.1ms</h3>
            <p className=" font-medium text-3xl">Execution Speed</p>
          </div>
        </div>
      </section>

      {/* =========================================
          3. WHAT WE DO & RISK DISCLAIMER
          ========================================= */}
      <section className="py-24 max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-2 gap-16 items-start">
          
          {/* About Platform */}
          <div>
            <h2 className="text-3xl font-black text-slate-900 mb-6">Motilal Algo Kya Hai?</h2>
            <p className="text-slate-600 text-lg leading-relaxed mb-6">
              Motilal Algo ek state-of-the-art algorithmic trading bridge hai. Hum traders ko advance technology provide karte hain jisse wo apni strategies ko bina manually screen dekhe execute kar sakein.
            </p>
            <ul className="space-y-4">
              {[
                "100% Automated Trading Execution",
                "Connects directly with your Demat Account",
                "Emotion-less Trading System",
                "High-speed data fetching & order placement"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                  <CheckCircle className="text-green-500" size={20} /> {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Legal & Risk Box (Crucial for Trading) */}
          <div className="bg-orange-50 border border-orange-200 rounded-3xl p-8 shadow-lg shadow-orange-100/50">
            <div className="flex items-center gap-3 mb-4">
              <Scale className="text-orange-600" size={28} />
              <h3 className="text-2xl font-black text-slate-900">Legal & Risk Disclaimer</h3>
            </div>
            <p className="text-slate-700 mb-4 leading-relaxed font-medium">
              Algo trading ek software tool hai. Iska use karne ka matlab ye nahi ki profit ki 100% guarantee hai. Market hamesha unpredictable hota hai.
            </p>
            <div className="bg-white p-4 rounded-xl border border-orange-100 flex items-start gap-3">
              <AlertTriangle className="text-red-500 shrink-0 mt-1" size={24} />
              <p className="text-sm text-slate-600 font-semibold leading-relaxed">
                <span className="text-red-600 font-bold">WARNING:</span> Stock Market investments are subject to market risks. Please read all related documents carefully before investing. <br/>
                Kripya apni research khud karein aur apne financial advisor se salah lein. Hum kisi bhi loss ke liye zimmewar nahi hain. Apne risk par trade karein.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* =========================================
          4. IMAGE GALLERY SHOWCASE
          ========================================= */}
      <section className="bg-slate-100 py-20">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-black text-slate-900 mb-10">Inside Our Ecosystem</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              "https://www.livemint.com/lm-img/img/2026/04/10/original/Oil_1775814847549_1775814853979.png", // Dashboard
              "https://img.freepik.com/free-photo/financial-data-visualization-stock-market-charts_23-2151977852.jpg?semt=ais_hybrid&w=740&q=80", // Charts
              "https://s44783.pcdn.co/in/wp-content/uploads/sites/3/2025/11/7-Highest-Paid-AI-Skills-for-2026-768x402.png", // Markets
              "https://png.pngtree.com/thumb_back/fh260/background/20230706/pngtree-illuminated-bull-hologram-dominates-forex-chart-in-prosperous-market-investing-and-image_3790197.jpg"  // Tech Team/Servers
            ].map((img, i) => (
              <motion.div 
                key={i}
                whileHover={{ scale: 1.05 }}
                className="overflow-hidden rounded-2xl shadow-md"
              >
                <img src={img} alt={`Gallery ${i+1}`} className="w-full h-48 object-cover hover:opacity-90 transition-opacity" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     
    </div>
  );
};

export default AboutUs;